
import Vue, { PropType } from 'vue';
import { ValidationConfig } from '../../validation';
import { mapGetters, mapState } from 'vuex';
import { getRowDetails } from '../../utils/repeater-widget';
import EntityList from '../../components/entity-list/entity-list.vue';
import { CustomComponentType } from '@sageworks/dynamic-forms';
import { CollateralUtils } from '@/utils';
import { PriorEntriesDisplay } from '@/models';
import ExistingEntrySelector from '../../components/existing-entry-selector/existing-entry-selector.vue';
import { Collateral } from '@sageworks/jpi';
import { CollateralWithCustomerName } from '../../../../../dynamic-forms/src/components/form-components/collateral-info/collateral-with-customer-name';
import ValidationAlert from '../../components/validation-alert/validation-alert.vue';
import { getCollateralDataObjectsDetails } from '../../../../../dynamic-forms/src/utils/collateral-role-utils/collateral-add-row-utils';

export default Vue.extend({
	name: 'CollateralInfo',
	components: { ValidationAlert, ExistingEntrySelector, EntityList },
	props: {
		componentLabel: {
			type: String,
			default: () => ''
		},
		validationConfig: {
			type: Object as PropType<ValidationConfig>
		},
		componentId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			collateralList: [] as any[],
			uniqueId: 0
		};
	},
	watch: {
		entityIds: {
			async handler() {
				this.collateralList = await this.collateralObjects();
			},
			immediate: true
		}
	},
	async mounted() {
		this.uniqueId = this._uid;
		this.collateralList = await this.collateralObjects();
	},
	computed: {
		...mapState('MultiLoanApplicationForm', ['renderData']),
		...mapGetters('User', ['isLender']),
		...mapGetters('MultiLoanApplicationForm', ['currentMetadata']),
		templateDataFieldMappings(): { [templateDataFieldId: number]: number } {
			return this.directStore.state.CustomComponentProperties.templateDataFieldIdToDataFieldId ?? {};
		},
		dataObjectsDetails() {
			return getCollateralDataObjectsDetails(this.renderData.formData.dataObjects);
		},
		rowDetails() {
			return getRowDetails(this.dataObjectsDetails, this.templateDataFieldMappings);
		},
		myComponentType() {
			return CustomComponentType.collateralInfo;
		},
		entityIds() {
			return this.directStore.state.LoanApplicationData.loanApplicationEntities.map((entity: PriorEntriesDisplay) => entity.id);
		},
		loanApplicationId() {
			return this.directStore.state.MultiLoanApplicationForm.loanApplicationId;
		},
		canAddMoreEntities(): boolean {
			return this.validationConfig.multiple || this.rowDetails.length === 0;
		},
		availableEntities(): PriorEntriesDisplay[] {
			return this.collateralList.map((collateral: Collateral) => {
				return {
					id: collateral.id,
					name: this.getLabel(collateral),
					type: 'collateral'
				};
			});
		},
		userIsNotAllowedToAddDeleteEntities(): boolean {
			if (this.isLender) {
				return false;
			}
			return this.directStore.getters.InstitutionSettings.borrowerIsNotAllowedToAddDeleteEntities;
		},
		userIsAllowedToAddNewEntities(): boolean {
			if (this.isLender) {
				return true;
			}
			return !this.directStore.getters.InstitutionSettings.borrowerIsNotAllowedToAddDeleteEntities;
		},
		isInvalid(): boolean {
			return this.directStore.getters.LoanApplicationData.componentContainValidationError(this.currentMetadata.loanMappingId, this.componentId);
		},
		showExistingEntitySelector(): boolean {
			return !this.userIsNotAllowedToAddDeleteEntities && this.availableEntities.length > 0;
		}
	},
	methods: {
		async collateralObjects(): Promise<CollateralWithCustomerName[]> {
			return await CollateralUtils.getCollateralForEntitiesWithCustomerName(this.entityIds ?? [], this.loanApplicationId!, this.isLender);
		},
		getLabel(collateral: CollateralWithCustomerName) {
			let label = collateral.description;
			if (this.isLender) {
				label += ' (';
				switch (collateral.collateralMetadataType) {
					case Collateral.CollateralMetadataTypeEnum.Proposed: {
						label += 'Proposed';
						break;
					}
					case Collateral.CollateralMetadataTypeEnum.Existing: {
						label += 'Existing';
						break;
					}
				}
				label += ')';
			}

			// might need to get the collateral owners name if it isn't this beneficiary value
			label += ' - ' + collateral.customerName;
			return label;
		},
		handleEntrySelected(data: any) {
			let collateral = this.collateralList.find((collateral: any) => collateral.id === data.id);
			this.$emit('add-existing-entry', collateral);
		}
	}
});
